<template>
    <v-app id="app">
        <TheHeader v-if="enableNavigation" :isOnline="isOnline" />
        <v-main >
            <v-container :class="{ 'fill-height': isLoginPage }">
                <GlobalDialog/>
                <GlobalSnackbar/>
                <router-view/>
            </v-container>
        </v-main>
    </v-app>
</template>

<script>
// IDB
import MailServices from '@/idb/MailServices';

// Routes
import LoginRoute from '@/router/routes/Login';

// Utils
import DataUtil from '@/utils/DataUtil';
import ValidationUtil from '@/utils/ValidationUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';
import ErrorUtil from '@/utils/ErrorUtil';

// Components
import TheHeader from '@/components/TheHeader';
import GlobalDialog from '@/components/GlobalDialog';
import GlobalSnackbar from '@/components/GlobalSnackbar';

export default {
    name: 'App',
    components: {
        TheHeader,
        GlobalDialog,
        GlobalSnackbar,
    },
    data() {
        return {
            isOnline: navigator.onLine,
            isFiringStoredActions: false,
        };
    },
    computed: {
        /** @returns { Boolean } */
        isLoginPage() {
            return this.$route.path.includes( 'login' );
        },
        /** @returns { Boolean } */
        enableNavigation() {
            return this.$route.name !== LoginRoute.name
                && this.$route.name
                && LoginRoute.name;
        },
    },
    watch: {
        isOnline() {
            if ( this.isOnline ) {
                this.fireStoredActions();
            }
        },
    },
    mounted() {
        this.pollIsOnline();
    },
    methods: {
        pollIsOnline() {
            setInterval( () => {
                this.isOnline = navigator.onLine;
            }, 500 );
        },
        /**
         * @param { String } actionType
         * @returns { Boolean }
         */
        async isReplayedActionLastOfType( actionType ) {
            const remainingActions = await MailServices.getAllActions();
            const remainingUniqueActions = [ ...new Set( remainingActions.map( ( remainingAction ) => remainingAction.type ) ) ];
            return !remainingUniqueActions.includes( actionType );
        },
        async fireStoredActions() {
            const storedActions = await MailServices.getAllActions();
            if ( ValidationUtil.arrayHasItems( storedActions ) ) {
                this.isFiringStoredActions = true;
                await DataUtil.asyncForEach( storedActions, async ( storedAction ) => {
                    try {
                        await this.$store.dispatch(
                            storedAction.type,
                            {
                                ...storedAction.payload,
                                isReplay: true,
                            },
                        );
                        await MailServices.removeAction( storedAction );

                        // If we successfully complete all of a given type of action, play the sweet sound of success
                        const isReplayedActionLastOfType = await this.isReplayedActionLastOfType( storedAction.type );
                        if ( isReplayedActionLastOfType ) {
                            await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                            await this.$store.dispatch( 'snackbar', {
                                message: 'Successfully completed pending deliveries.',
                                type: 'success',
                                duration: 5000,
                            }, { root: true } );
                        }
                    } catch ( error ) {
                        console.error( 'Error in fireStoredActions: ', error );
                        await AudioUtil.playSound( soundNames.FAILURE_LONG );
                        await this.$store.dispatch( 'snackbar', {
                            message: `Error completing pending deliveries: ${ ErrorUtil.getMessageFromError( error ) }`,
                            type: 'error',
                            duration: 5000,
                        }, { root: true } );
                    }
                } );
                this.isFiringStoredActions = false;
            }
        },
    },
};

</script>

<style lang="scss">
#app .tab-container {
    border: 8px solid $colors-grey-lighten-3;
    .v-tabs-bar {
        background-color: $colors-grey-lighten-3;
    }
    .v-tab--active {
        &::before {
            border-radius: 4px 4px 0 0;
        }
        border-radius: 4px 4px 0 0;
        background-color: white;
    }
}

.logo {
    width: $variables-logo-width;
}

.breadcrumb,
.tabs{
  font-family: $typography-brother-1816;
}

body,
button,
input,
select,
textarea{
  font-family: $typography-open-sans;
}
</style>
